import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Chip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, TablePagination, InputAdornment, Select,
  MenuItem, FormControl, InputLabel, Tooltip, Snackbar, Alert,
  CircularProgress, Switch, FormControlLabel, Checkbox,
} from "@mui/material";
import {
  Add, Edit, Delete, Search, Close, Category,
  QuestionAnswer, Refresh,
} from "@mui/icons-material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import { BASE_URL } from "../services/configUrls";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563eb", contrastText: "#ffffff" },
    secondary: { main: "#7c3aed" },
    background: { default: "#f1f5f9", paper: "#ffffff" },
    text: { primary: "#0f172a", secondary: "#64748b" },
    success: { main: "#16a34a" },
    error: { main: "#dc2626" },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily: "'DM Sans', 'Inter', sans-serif",
    fontSize: 12,                        // ← base font size reduced from 14 to 12
    h4: { fontWeight: 800, letterSpacing: "-0.5px", fontSize: "1.25rem" }, // ← was ~2.125rem
    h5: { fontWeight: 700, fontSize: "1rem" },
    h6: { fontWeight: 700, fontSize: "0.875rem" },
    body1: { fontSize: "0.8rem" },       // ← was 0.875rem–1rem
    body2: { fontSize: "0.75rem" },      // ← was 0.8rem
    subtitle1: { fontSize: "0.8rem" },
    subtitle2: { fontSize: "0.75rem" },
    caption: { fontSize: "0.7rem" },
    overline: { fontSize: "0.65rem" },
    button: { fontSize: "0.75rem", textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px 0 rgba(15,23,42,0.06)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          fontSize: "0.75rem",     // ← smaller button text
          padding: "4px 12px",     // ← tighter padding
        },
        containedPrimary: { color: "#ffffff" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#f8fafc",
          color: "#2563eb",
          fontWeight: 700,
          fontSize: "0.65rem",     // ← was 0.78rem
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          borderBottom: "2px solid #e2e8f0",
          padding: "8px 12px",    // ← tighter cell padding
        },
        body: {
          borderColor: "#f1f5f9",
          color: "#0f172a",
          fontSize: "0.75rem",    // ← explicit small body cell size
          padding: "6px 12px",   // ← tighter cell padding
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8rem",      // ← was 0.95rem
          minHeight: 40,
          padding: "6px 14px",
          color: "#64748b",
          "&.Mui-selected": { color: "#2563eb" },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          "& input": { fontSize: "0.78rem" },         // ← smaller input text
          "& textarea": { fontSize: "0.78rem" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: "#64748b", fontSize: "0.78rem" }, // ← smaller label
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { fontSize: "0.78rem" },               // ← smaller select text
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "0.78rem" },                 // ← smaller menu items
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "0.65rem",    // ← was 0.72rem
          height: 20,             // ← shorter chip height
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          fontSize: "0.78rem",
          "& fieldset": { borderColor: "#e2e8f0" },
          "&:hover fieldset": { borderColor: "#94a3b8" },
          "&.Mui-focused fieldset": { borderColor: "#2563eb" },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: "#ffffff", border: "1px solid #e2e8f0" },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: "0.875rem", fontWeight: 700, padding: "14px 20px 12px" },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "12px 20px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: "10px 20px 14px" },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#64748b",
          padding: 6,             // ← tighter icon button
          "&:hover": { backgroundColor: "#f1f5f9" },
          "& svg": { fontSize: "1rem" }, // ← smaller icons
        },
        sizeSmall: {
          padding: 4,
          "& svg": { fontSize: "0.9rem" },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:hover": { backgroundColor: "#f8fafc !important" } },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: "0.72rem", color: "#64748b" },
        selectLabel: { fontSize: "0.72rem" },
        displayedRows: { fontSize: "0.72rem" },
        select: { fontSize: "0.72rem" },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: "0.78rem" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.68rem" },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { fontSize: "0.75rem" },
      },
    },
  },
});

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}/chatbot${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...opts,
  });
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "API error");
  return data;
}

function useSnack() {
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const show = (msg, severity = "success") => setSnack({ open: true, msg, severity });
  const hide = () => setSnack((s) => ({ ...s, open: false }));
  return { snack, show, hide };
}

function CategoriesTab({ onCategoryChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: "add", row: null });
  const [form, setForm] = useState({ category_name: "", is_active: 1 });
  const [deleteConf, setDeleteConf] = useState({ open: false, row: null });
  const [selected, setSelected] = useState([]);
  const { snack, show, hide } = useSnack();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page + 1, page_size: pageSize });
      if (search) params.append("search", search);
      if (filterActive !== "") params.append("is_active", filterActive);
      const data = await apiFetch(`/admin/categories?${params}`);
      setRows(data.items);
      setTotal(data.total);
      setSelected([]);
    } catch (e) { show(e.message, "error"); }
    finally { setLoading(false); }
  }, [page, pageSize, search, filterActive]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ category_name: "", is_active: 1 }); setDialog({ open: true, mode: "add", row: null }); };
  const openEdit = (row) => { setForm({ category_name: row.category_name, is_active: row.is_active }); setDialog({ open: true, mode: "edit", row }); };
  const closeDialog = () => setDialog({ open: false, mode: "add", row: null });

  const handleSave = async () => {
    try {
      if (dialog.mode === "add") {
        await apiFetch("/admin/categories", { method: "POST", body: JSON.stringify(form) });
        show("Category created!");
      } else {
        await apiFetch(`/admin/categories/${dialog.row.category_id}`, { method: "PUT", body: JSON.stringify(form) });
        show("Category updated!");
      }
      closeDialog(); load(); onCategoryChange?.();
    } catch (e) { show(e.message, "error"); }
  };

  const handleDelete = async () => {
    try {
      await apiFetch(`/admin/categories/${deleteConf.row.category_id}`, { method: "DELETE" });
      show("Category deleted.", "warning");
      setDeleteConf({ open: false, row: null }); load(); onCategoryChange?.();
    } catch (e) { show(e.message, "error"); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(rows.map((r) => r.category_id));
    else setSelected([]);
  };

  const handleSelectOne = (e, id) => {
    if (e.target.checked) setSelected((prev) => [...prev, id]);
    else setSelected((prev) => prev.filter((item) => item !== id));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selected.length} categories?`)) return;
    try {
      await apiFetch("/admin/categories/bulk-delete", { method: "POST", body: JSON.stringify({ category_ids: selected }) });
      show(`${selected.length} categories deleted.`, "warning");
      setSelected([]);
      load();
      onCategoryChange?.();
    } catch (e) { show(e.message, "error"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder="Search categories…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 15, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 210 }}
        />
        <FormControl size="small" sx={{ width: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterActive} label="Status" onChange={(e) => { setFilterActive(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={load} size="small"><Refresh sx={{ fontSize: "0.9rem" }} /></IconButton>
        </Tooltip>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          {selected.length > 0 && (
            <Button variant="contained" color="error" startIcon={<Delete sx={{ fontSize: "0.85rem !important" }} />} onClick={handleBulkDelete}>
              Delete ({selected.length})
            </Button>
          )}
          <Button variant="contained" startIcon={<Add sx={{ fontSize: "0.85rem !important" }} />} onClick={openAdd}>
            Add Category
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={rows.length > 0 && selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length} onChange={handleSelectAll} />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Category Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} color="primary" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary", fontSize: "0.75rem" }}>
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.category_id} hover selected={selected.includes(row.category_id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(row.category_id)} onChange={(e) => handleSelectOne(e, row.category_id)} />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.7rem" }}>#{row.category_id}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>{row.category_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_active ? "Active" : "Inactive"}
                      color={row.is_active ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#2563eb" }}>
                        <Edit sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteConf({ open: true, row })}>
                        <Delete sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ borderTop: "1px solid #e2e8f0" }}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
            {dialog.mode === "add" ? "Add Category" : "Edit Category"}
          </Typography>
          <IconButton size="small" onClick={closeDialog}><Close sx={{ fontSize: "0.9rem" }} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1.5 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={form.category_name}
              onChange={(e) => setForm({ ...form, category_name: e.target.value })}
              sx={{ mb: 1.5 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={form.is_active === 1}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={closeDialog} color="inherit" sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.category_name.trim()}>
            {dialog.mode === "add" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConf.open} onClose={() => setDeleteConf({ open: false, row: null })} maxWidth="xs">
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>Delete Category?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", pt: 1.5 }}>
            Are you sure you want to delete{" "}
            <strong style={{ color: "#0f172a" }}>{deleteConf.row?.category_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={() => setDeleteConf({ open: false, row: null })} color="inherit" sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={hide} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={hide} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

function IssuesTab({ categories }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [domains, setDomains] = useState([]);
  const [filterDomain, setFilterDomain] = useState("");
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: "add", row: null });
  const blankForm = { category_id: "", issue: "", solution: "", domain_id: "", display_order: "", is_active: 1 };
  const [form, setForm] = useState(blankForm);
  const [deleteConf, setDeleteConf] = useState({ open: false, row: null });
  const [selected, setSelected] = useState([]);
  const { snack, show, hide } = useSnack();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page + 1, page_size: pageSize });
      if (search) params.append("search", search);
      if (filterCat !== "") params.append("category_id", filterCat);
       if (filterDomain !== "") params.append("domain_id", filterDomain);
      if (filterActive !== "") params.append("is_active", filterActive);
      const data = await apiFetch(`/admin/issues?${params}`);
      setRows(data.items);
      setTotal(data.total);
      setSelected([]);
    } catch (e) { show(e.message, "error"); }
    finally { setLoading(false); }
  }, [page, pageSize, search, filterCat,filterDomain, filterActive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
  (async () => {
    try {
      const data = await apiFetch("/admin/domains");
      setDomains(data); // API returns a plain array: [{domain_id, domain_name}, ...]
    } catch (e) {
      show(e.message, "error");
    }
  })();
}, []);

  const openAdd = () => { setForm(blankForm); setDialog({ open: true, mode: "add", row: null }); };
  const openEdit = (row) => {
    setForm({
      category_id: row.category_id ?? "",
      issue: row.issue ?? "",
      solution: row.solution ?? "",
      domain_id: row.domain_id ?? "",
      display_order: row.display_order ?? "",
      is_active: row.is_active ?? 1,
    });
    setDialog({ open: true, mode: "edit", row });
  };
  const closeDialog = () => setDialog({ open: false, mode: "add", row: null });

  const handleSave = async () => {
    const payload = {
      category_id: Number(form.category_id),
      issue: form.issue,
      solution: form.solution || null,
      domain_id: form.domain_id !== "" ? Number(form.domain_id) : null,
      display_order: form.display_order !== "" ? Number(form.display_order) : null,
      is_active: form.is_active,
    };
    try {
      if (dialog.mode === "add") {
        await apiFetch("/admin/issues", { method: "POST", body: JSON.stringify(payload) });
        show("Issue created!");
      } else {
        await apiFetch(`/admin/issues/${dialog.row.issue_id}`, { method: "PUT", body: JSON.stringify(payload) });
        show("Issue updated!");
      }
      closeDialog(); load();
    } catch (e) { show(e.message, "error"); }
  };

  const handleDelete = async () => {
    try {
      await apiFetch(`/admin/issues/${deleteConf.row.issue_id}`, { method: "DELETE" });
      show("Issue deleted.", "warning");
      setDeleteConf({ open: false, row: null }); load();
    } catch (e) { show(e.message, "error"); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(rows.map((r) => r.issue_id));
    else setSelected([]);
  };

  const handleSelectOne = (e, id) => {
    if (e.target.checked) setSelected((prev) => [...prev, id]);
    else setSelected((prev) => prev.filter((item) => item !== id));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selected.length} issues?`)) return;
    try {
      await apiFetch("/admin/issues/bulk-delete", { method: "POST", body: JSON.stringify({ issue_ids: selected }) });
      show(`${selected.length} issues deleted.`, "warning");
      setSelected([]);
      load();
    } catch (e) { show(e.message, "error"); }
  };

  const catName = (id) => categories.find((c) => c.category_id === id)?.category_name ?? `#${id}`;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder="Search issues…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 15, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 210 }}
        />
        <FormControl size="small" sx={{ width: 155 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCat} label="Category" onChange={(e) => { setFilterCat(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 155 }}>
  <InputLabel>Domain</InputLabel>
  <Select
    value={filterDomain}
    label="Domain"
    onChange={(e) => { setFilterDomain(e.target.value); setPage(0); }}
  >
    <MenuItem value="">All</MenuItem>
    {domains.map((d) => (
      <MenuItem key={d.domain_id} value={d.domain_id}>{d.domain_name}</MenuItem>
    ))}
  </Select>
</FormControl>
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterActive} label="Status" onChange={(e) => { setFilterActive(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={load} size="small"><Refresh sx={{ fontSize: "0.9rem" }} /></IconButton>
        </Tooltip>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          {selected.length > 0 && (
            <Button variant="contained" color="error" startIcon={<Delete sx={{ fontSize: "0.85rem !important" }} />} onClick={handleBulkDelete}>
              Delete ({selected.length})
            </Button>
          )}
          <Button variant="contained" startIcon={<Add sx={{ fontSize: "0.85rem !important" }} />} onClick={openAdd}>
            Add Issue
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={rows.length > 0 && selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length} onChange={handleSelectAll} />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Issue</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Domain ID</TableCell>
              <TableCell>Domain Name</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} color="primary" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary", fontSize: "0.75rem" }}>
                  No issues found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.issue_id} hover selected={selected.includes(row.issue_id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(row.issue_id)} onChange={(e) => handleSelectOne(e, row.issue_id)} />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.7rem" }}>#{row.issue_id}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Tooltip title={row.issue}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280, fontSize: "0.75rem" }}
                      >
                        {row.issue}
                      </Typography>
                    </Tooltip>
                    {row.solution && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280, fontSize: "0.68rem" }}
                      >
                        ↳ {row.solution}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={catName(row.category_id)}
                      size="small"
                      sx={{
                        bgcolor: alpha("#2563eb", 0.08),
                        color: "#2563eb",
                        border: "1px solid rgba(37,99,235,0.25)",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{row.domain_id ?? "—"}</TableCell>
                  <TableCell>{row.domain_name ?? "--"}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{row.display_order ?? "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_active ? "Active" : "Inactive"}
                      color={row.is_active ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#2563eb" }}>
                        <Edit sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteConf({ open: true, row })}>
                        <Delete sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ borderTop: "1px solid #e2e8f0" }}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
            {dialog.mode === "add" ? "Add Issue" : "Edit Issue"}
          </Typography>
          <IconButton size="small" onClick={closeDialog}><Close sx={{ fontSize: "0.9rem" }} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category *</InputLabel>
              <Select value={form.category_id} label="Category *" onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                {categories.map((c) => (
                  <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField fullWidth label="Issue *" multiline rows={2} value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} />
            <TextField
              fullWidth
              label="Solution"
              multiline
              rows={3}
              value={form.solution}
              onChange={(e) => setForm({ ...form, solution: e.target.value })}
              placeholder="You can use {{REGISTRATION_END_DATE}} as a placeholder…"
            />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField label="Domain ID" type="number" value={form.domain_id} onChange={(e) => setForm({ ...form, domain_id: e.target.value })} sx={{ flex: 1 }} />
              <TextField label="Display Order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} sx={{ flex: 1 }} />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={form.is_active === 1}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={closeDialog} color="inherit" sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.issue.trim() || !form.category_id}>
            {dialog.mode === "add" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConf.open} onClose={() => setDeleteConf({ open: false, row: null })} maxWidth="xs">
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>Delete Issue?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", pt: 1.5 }}>
            Are you sure you want to delete this issue? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={() => setDeleteConf({ open: false, row: null })} color="inherit" sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={hide} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={hide} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

export default function ChatbotAdmin() {
  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiFetch("/admin/categories?page=1&page_size=100");
      setCategories(data.items);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 34, height: 34, borderRadius: "8px",
                background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
              }}
            >
              <QuestionAnswer sx={{ fontSize: 17, color: "#ffffff" }} />
            </Box>
            <Typography
              variant="h4"
              sx={{ background: "linear-gradient(90deg, #2563eb, #7c3aed)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >
              Chatbot Admin
            </Typography>
          </Box>
          <Typography sx={{ ml: "46px", fontSize: "0.75rem", color: "text.secondary" }}>
            Manage chatbot categories, issues, and solutions
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2.5 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTabs-indicator": {
                background: "linear-gradient(90deg, #2563eb, #7c3aed)",
                height: 2.5,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab icon={<Category sx={{ fontSize: 15 }} />} iconPosition="start" label="Categories" />
            <Tab icon={<QuestionAnswer sx={{ fontSize: 15 }} />} iconPosition="start" label="Issues & Solutions" />
          </Tabs>
        </Paper>

        <Box hidden={tab !== 0}><CategoriesTab onCategoryChange={fetchCategories} /></Box>
        <Box hidden={tab !== 1}><IssuesTab categories={categories} /></Box>
      </Box>
    </ThemeProvider>
  );
}